<template>
  <div class="complete-page">
    <HeaderBar />

    <main class="content">
      <div class="intro">
        <div class="intro-copy">
          <h1 class="title">Where would you like to go?</h1>
        </div>
        <div v-if="currentUserEmail" class="signed-in-banner">
          <span class="signed-in-label">Signed in as</span>
          <strong class="signed-in-value">{{ currentUserEmail }}</strong>
        </div>
        <el-button type="primary" size="large" class="logout-btn" @click="logout">
          Log out
        </el-button>
      </div>

      <div class="card-grid">
        <el-card
          class="portal-card portal-employee"
          :class="{ loading: employeeLoading }"
          shadow="hover"
          role="link"
          :aria-disabled="employeeLoading"
          :tabindex="employeeLoading ? -1 : 0"
          @click="goToEmployeePortal"
          @keyup.enter="goToEmployeePortal"
          @keyup.space.prevent="goToEmployeePortal"
        >
          <p class="card-label">Employee Portal</p>
          <p class="card-text">{{ employeePortalText }}</p>
          <span class="card-cta">{{ employeeLoading ? 'Checking access...' : 'Continue ->' }}</span>
        </el-card>

        <el-card
          class="portal-card portal-submission"
          :class="{ loading: customerLoading, disabled: isPrivilegedUser }"
          shadow="hover"
          role="link"
          :aria-disabled="customerLoading || isPrivilegedUser"
          :tabindex="customerLoading || isPrivilegedUser ? -1 : 0"
          @click="goToCustomerPortal"
          @keyup.enter="goToCustomerPortal"
          @keyup.space.prevent="goToCustomerPortal"
        >
          <p class="card-label">Customer Portal</p>
          <p class="card-text">{{ customerPortalText }}</p>
          <span class="card-cta">{{ customerLoading ? 'Opening...' : 'Open portal ->' }}</span>
        </el-card>
      </div>
    </main>
  </div>
</template>

<script>
import HeaderBar from '@/components/HeaderBar.vue'
import { auth } from '@/firebase'
import { getUserRole } from '@/services/requests'
import { getEmployeePortalAccess, isCustomerProfileComplete, resolveUserProfile } from '@/services/userProfile'
import { ElMessage } from 'element-plus'
import { signOut } from 'firebase/auth'

export default {
  name: 'CompleteProfileView',
  components: { HeaderBar },
  data() {
    return {
      submissionLink: 'https://forms.office.com/r/GZr02BuDGS?origin=lprLink',
      role: '',
      currentUserEmail: '',
      employeeLoading: false,
      customerLoading: false,
    }
  },
  computed: {
    isOwnerUser() {
      return this.role === 'Owner'
    },
    isEmployeeUser() {
      return this.role === 'Admin'
    },
    isPrivilegedUser() {
      return this.isEmployeeUser || this.isOwnerUser
    },
    customerPortalText() {
      return this.isPrivilegedUser
        ? 'Customer dashboard access is reserved for customer accounts.'
        : 'Complete your customer profile and manage submissions from the dashboard.'
    },
    employeePortalText() {
      if (this.isOwnerUser) {
        return 'Open the employee dashboard. Owner management actions are available from the Owner Tools tab.'
      }
      return this.isEmployeeUser
        ? 'Continue to the employee dashboard or your current approval state.'
        : 'Apply for employee access or continue your current employee onboarding step.'
    }
  },
  async mounted() {
    const user = auth.currentUser
    const uid = user?.uid
    this.currentUserEmail = user?.email || ''
    this.role = await getUserRole(uid)
    if (this.role === 'Owner') {
      this.$router.replace({ name: 'Dashboard' })
    }
  },
  methods: {
    async goToEmployeePortal() {
      if (this.employeeLoading) return
      this.employeeLoading = true

      try {
        const user = auth.currentUser
        if (!user) {
          this.$router.push({ name: 'Login' })
          return
        }

        const { routeName } = await getEmployeePortalAccess(user)
        await this.$router.push({ name: routeName })
      } catch (error) {
        ElMessage({ type: 'error', message: error?.message || 'Unable to open the Employee Portal right now.' })
      } finally {
        this.employeeLoading = false
      }
    },
    async goToCustomerPortal() {
      if (this.customerLoading || this.isPrivilegedUser) return

      const user = auth.currentUser
      if (!user) {
        this.$router.push({ name: 'Login' })
        return
      }

      this.customerLoading = true
      try {
        const profile = await resolveUserProfile(user)
        const shouldPromptProfile = !isCustomerProfileComplete(profile)
        await this.$router.push({
          name: 'Dashboard',
          query: shouldPromptProfile ? { customerProfile: '1' } : {},
        })
      } catch (error) {
        ElMessage({ type: 'error', message: error?.message || 'Unable to open the Customer Portal right now.' })
      } finally {
        this.customerLoading = false
      }
    },
    async logout() {
      try {
        await signOut(auth)
      } catch (e) {}
      this.$router.push({ name: 'Login' })
    }
  }
}
</script>

<style scoped>
.complete-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f6fbff 0%, #e9f2ff 100%);
  color: #03241f;
}

.content {
  max-width: 1100px;
  margin: 36px auto 64px;
  padding: 0 16px;
  box-sizing: border-box;
}

.intro {
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.intro-copy {
  min-width: 0;
  margin-right: auto;
}

.title {
  margin: 0 0 8px;
  font-size: 2rem;
  font-weight: 800;
  color: #03241f;
}

.signed-in-banner {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 14px;
  border: 1px solid rgba(11, 108, 255, 0.18);
  background: linear-gradient(135deg, rgba(11, 108, 255, 0.12), rgba(8, 145, 178, 0.08));
  box-shadow: 0 10px 24px rgba(11, 108, 255, 0.08);
  color: #0a355f;
  cursor: default;
  user-select: text;
}

.signed-in-label {
  font-size: 0.85rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #0b6cff;
}

.signed-in-value {
  font-size: 1rem;
  font-weight: 750;
  color: #03241f;
  word-break: break-word;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 18px;
}

.portal-card {
  border-radius: 18px;
  box-shadow: 0 18px 44px rgba(3, 36, 31, 0.12);
  border: 1px solid rgba(3, 36, 31, 0.12);
  cursor: pointer;
  transition: transform 140ms ease, box-shadow 140ms ease, background-color 140ms ease, color 140ms ease;
  background: #003831;
  color: #f6fff6;
  position: relative;
}

.portal-card.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.portal-card.loading {
  opacity: 0.85;
  cursor: progress;
}

.portal-card:focus-visible {
  outline: 3px solid rgba(11, 92, 255, 0.4);
  outline-offset: 3px;
}

.portal-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 20px 50px rgba(3, 36, 31, 0.18);
  background: #005d52;
}

.portal-employee,
.portal-submission {
  background: #003831;
}

.card-label {
  margin: 0 0 6px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  font-size: 0.9rem;
  color: #d5f5ea;
}

.card-text {
  margin: 0 0 14px;
  font-size: 1rem;
  color: #fffbf5;
}

.card-cta {
  font-weight: 700;
  color: #fffbf5;
}

.portal-card:hover .card-text,
.portal-card:hover .card-cta {
  color: #fffbf5;
}

.logout-btn {
  margin-left: 0;
}

@media (max-width: 600px) {
  .content {
    margin-top: 28px;
  }

  .title {
    font-size: 1.7rem;
  }
}
</style>
